// @ts-check

import { digitMask } from "../engine/constants.js";
import { conflictsFor } from "../engine/board.js";
import { PEERS_BY_CELL, UNITS_BY_CELL } from "../engine/topology.js";

export function createGame(puzzle, options = {}) {
  const values = puzzle.givens;
  const now = options.now?.() ?? new Date();
  return {
    state_version: 1,
    puzzle_id: puzzle.id,
    values,
    notes: Array(81).fill(0),
    selected_cell: firstEditable(puzzle.givens),
    note_mode: false,
    elapsed_active_ms: 0,
    timer_running: true,
    paused: false,
    mistakes: 0,
    hints_used: 0,
    reveals_used: 0,
    reveal_progress: 0,
    started_at: now.toISOString(),
    last_active_at: now.toISOString(),
    completed_at: null,
    history: [],
    redo: [],
    action_count: 0,
    daily_date: options.dailyDate ?? null,
    companion_state: null,
    hint: null,
    hint_stage: 0,
    occupancy_milestones: [],
    completed_units: [],
  };
}

export function restoreGame(record) {
  return {
    ...record,
    notes: Array.from(record.notes ?? Array(81).fill(0)),
    history: record.history ?? [],
    redo: record.redo ?? [],
    occupancy_milestones: record.occupancy_milestones ?? [],
    completed_units: record.completed_units ?? [],
  };
}

export function selectCell(game, cell) {
  return { ...game, selected_cell: Math.max(0, Math.min(80, cell)) };
}
export function toggleNotes(game) {
  return {
    ...game,
    note_mode: !game.note_mode,
    action_count: game.action_count + 1,
  };
}

export function placeDigit(
  game,
  puzzle,
  digit,
  settings,
  now = () => new Date(),
) {
  const cell = game.selected_cell;
  if (puzzle.givens[cell] !== "0" || game.completed_at || game.paused)
    return game;
  const previous = snapshotCell(game, cell);
  const notes = [...game.notes];
  let values = game.values;
  const peerChanges = [];
  if (game.note_mode) {
    notes[cell] ^= digitMask(digit);
  } else {
    values = replaceAt(
      values,
      cell,
      values[cell] === String(digit) ? "0" : String(digit),
    );
    notes[cell] = 0;
    if (settings.autoRemovePeerNotes && values[cell] !== "0") {
      for (const peer of PEERS_BY_CELL[cell]) {
        const before = notes[peer];
        notes[peer] &= ~digitMask(digit);
        if (before !== notes[peer])
          peerChanges.push({ cell: peer, before, after: notes[peer] });
      }
    }
  }
  const mismatch =
    !game.note_mode &&
    values[cell] !== "0" &&
    values[cell] !== puzzle.solution[cell];
  const revealProgress = calculateReveal(game.reveal_progress, values, puzzle);
  const command = {
    type: game.note_mode ? "note" : "place",
    cell,
    before: previous,
    after: { value: values[cell], notes: notes[cell] },
    peer_changes: peerChanges,
    reveal_before: game.reveal_progress,
    reveal_after: revealProgress,
  };
  return {
    ...game,
    values,
    notes,
    reveal_progress: revealProgress,
    mistakes:
      game.mistakes + (settings.checkAgainstSolution && mismatch ? 1 : 0),
    history: [...game.history, command].slice(-300),
    redo: [],
    action_count: game.action_count + 1,
    last_active_at: now().toISOString(),
  };
}

export function erase(game, puzzle) {
  const cell = game.selected_cell;
  if (puzzle.givens[cell] !== "0" || game.completed_at || game.paused)
    return game;
  const command = {
    type: "erase",
    cell,
    before: snapshotCell(game, cell),
    after: { value: "0", notes: 0 },
    peer_changes: [],
    reveal_before: game.reveal_progress,
    reveal_after: game.reveal_progress,
  };
  return {
    ...game,
    values: replaceAt(game.values, cell, "0"),
    notes: game.notes.map((value, index) => (index === cell ? 0 : value)),
    history: [...game.history, command].slice(-300),
    redo: [],
    action_count: game.action_count + 1,
  };
}

export function undo(game) {
  const command = game.history.at(-1);
  if (!command || game.completed_at) return game;
  let notes = [...game.notes];
  let values = replaceAt(game.values, command.cell, command.before.value);
  notes[command.cell] = command.before.notes;
  for (const change of command.peer_changes) notes[change.cell] = change.before;
  return {
    ...game,
    values,
    notes,
    reveal_progress: Math.max(game.reveal_progress, command.reveal_before),
    history: game.history.slice(0, -1),
    redo: [...game.redo, command],
    action_count: game.action_count + 1,
  };
}

export function redo(game) {
  const command = game.redo.at(-1);
  if (!command || game.completed_at) return game;
  let notes = [...game.notes];
  const values = replaceAt(game.values, command.cell, command.after.value);
  notes[command.cell] = command.after.notes;
  for (const change of command.peer_changes) notes[change.cell] = change.after;
  return {
    ...game,
    values,
    notes,
    reveal_progress: Math.max(game.reveal_progress, command.reveal_after),
    history: [...game.history, command],
    redo: game.redo.slice(0, -1),
    action_count: game.action_count + 1,
  };
}

export function revealCell(game, puzzle, now = () => new Date()) {
  const cell = game.selected_cell;
  if (puzzle.givens[cell] !== "0" || game.completed_at) return game;
  const next = placeDigit(
    { ...game, note_mode: false },
    puzzle,
    Number(puzzle.solution[cell]),
    { autoRemovePeerNotes: true, checkAgainstSolution: false },
    now,
  );
  return { ...next, reveals_used: game.reveals_used + 1 };
}

export function applyHint(game, puzzle, hint, now = () => new Date()) {
  let next = {
    ...game,
    hints_used: game.hints_used + 1,
    hint: null,
    hint_stage: 0,
  };
  if (hint.placements?.length) {
    const placement = hint.placements[0];
    next = placeDigit(
      { ...next, selected_cell: placement.cell, note_mode: false },
      puzzle,
      placement.digit,
      { autoRemovePeerNotes: true, checkAgainstSolution: false },
      now,
    );
  } else {
    const notes = [...next.notes];
    for (const elimination of hint.eliminations ?? [])
      notes[elimination.cell] &= ~digitMask(elimination.digit);
    next = { ...next, notes, action_count: next.action_count + 1 };
  }
  return next;
}

export function conflictCells(game) {
  const board = Uint8Array.from(game.values, Number);
  const output = new Set();
  for (let cell = 0; cell < 81; cell += 1)
    for (const peer of conflictsFor(board, cell)) {
      output.add(cell);
      output.add(peer);
    }
  return output;
}

export function publicUnitCompletions(game) {
  const found = [];
  for (let cell = 0; cell < 81; cell += 1) {
    const units = ["row", "column", "box"].map((type, index) => ({
      type,
      cells: UNITS_BY_CELL[cell][index],
    }));
    for (const { type: unitType, cells: unit } of units) {
      const key = `${unitType}:${unit[0]}:${unit.at(-1)}`;
      if (
        !game.completed_units.includes(key) &&
        unit.every((item) => game.values[item] !== "0")
      )
        found.push({ key, event: `${unitType}_complete` });
    }
  }
  return [...new Map(found.map((item) => [item.key, item])).values()];
}

export function isComplete(game, puzzle) {
  return game.values === puzzle.solution;
}
export function pauseGame(game, paused) {
  return {
    ...game,
    paused,
    timer_running: !paused && !game.completed_at,
    action_count: game.action_count + 1,
  };
}
export function tickGame(game, milliseconds) {
  return !game.timer_running || game.paused || game.completed_at
    ? game
    : { ...game, elapsed_active_ms: game.elapsed_active_ms + milliseconds };
}

function firstEditable(givens) {
  return Math.max(
    0,
    [...givens].findIndex((value) => value === "0"),
  );
}
function snapshotCell(game, cell) {
  return { value: game.values[cell], notes: game.notes[cell] };
}
function replaceAt(value, index, character) {
  return `${value.slice(0, index)}${character}${value.slice(index + 1)}`;
}
function calculateReveal(current, values, puzzle) {
  const editable = [...puzzle.givens].filter((digit) => digit === "0").length;
  let correct = 0;
  for (let cell = 0; cell < 81; cell += 1)
    if (puzzle.givens[cell] === "0" && values[cell] === puzzle.solution[cell])
      correct += 1;
  const raw = editable ? correct / editable : 1;
  const stage =
    raw >= 1
      ? 1
      : raw >= 0.75
        ? 0.75
        : raw >= 0.5
          ? 0.5
          : raw >= 0.25
            ? 0.25
            : 0;
  return Math.max(current, stage);
}
